/* ═══════════════════════════════════════════════════════════════════
   [D-423] Демо-режим дашборду — «око» біля кнопки оновлення.

   Навіщо: показати дашборд сторонньому, не показуючи внутрішньої кухні.
   Вмикається однією кнопкою і:
     • ховає пункти «🎭 Фейкові юзери» і «Чорний список»;
     • підміняє у «Системі» справжні моделі й ціни серверів на одну
       нейтральну (Dell DX154-2, 900 €/міс);
     • перефарбовує фірмовий бузковий у неоново-блакитний.

   🔴 Стан живе ТІЛЬКИ в браузері (localStorage). Нічого не пишеться на
   сервер і нічого не змінюється для інших: це режим показу, а не
   налаштування системи. Вимкнув — усе повернулось, бо нічого й не
   чіпалось.
   ═══════════════════════════════════════════════════════════════════ */
import React from 'react';

const KEY = 'dash_demo_mode';
const EV = 'dash-demo-change';

// 🔴 Підставні характеристики — однакові для КОЖНОГО сервера. Різні
// вигадані ціни виглядали б як справжні дані, а мета протилежна.
export const DEMO_SERVER = {
  plan: 'Dell DX154-2',
  price: '900 €/mo',
};

/* Характеристики, які бачить гість. Однакові для КОЖНОГО сервера — різні
   вигадані конфігурації виглядали б як справжні дані, а мета протилежна. */
export const DEMO_SPECS = {
  cpu_model: 'Intel® Xeon® 6741P',
  cpu_cores: 48,
  ram_total_gb: 256,
  ram_type: 'DDR5 ECC reg',
  disks: '4 × 1.92 ТБ NVMe DC-Edition',
  disk_total_gb: 7680,
  net: '1 Гбіт/с · безлімітний трафік',
};

/* Колір цінника — навмисно кислотний: OWNER просив, щоб сума впадала в
   око з першого погляду, а не ховалась сірим підписом унизу картки. */
export const DEMO_ACID = '#d4ff00';

/* Живе навантаження.

   🔴 Числа мусять бути СТАЛИМИ між перемальовками і повільно повзти в
   часі. Сторінка оновлюється кожні 5 секунд: якби брали випадкове число,
   стовпчики стрибали б і одразу викривали підробку. Тому — синус від
   годинника з довгим періодом і зсувом фази на кожен сервер: виглядає як
   жива система під навантаженням, а не як генератор випадкових чисел.

   `seed` — будь-який рядок (ім'я сервера), щоб сусідні картки не дихали
   в такт. */
function wave(seed, base, amp, periodSec) {
  let h = 0;
  const str = String(seed || '');
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 9973;
  const t = Date.now() / 1000;
  const phase = (h % 1000) / 1000;
  const v = base + amp * Math.sin(2 * Math.PI * (t / periodSec + phase));
  return Math.round(v * 10) / 10;
}

/* Навантаження «під наплив юзерів»: високе, але не критичне — сервер на
   межі виглядав би як проблема, а не як популярний продукт. */
export function fakeLoad(seed) {
  return {
    cpu: wave(seed + 'c', 68, 14, 137),
    ram: wave(seed + 'r', 71, 9, 211),
    disk: wave(seed + 'd', 64, 2, 631),
    storage_pct: wave(seed + 's', 58, 6, 419),
    nfs_disk: wave(seed + 'n', 61, 5, 373),
    load_avg: wave(seed + 'l', 31, 7, 173),
    uptime_hours: 2880 + Math.floor((Date.now() / 3600000) % 24),
    disk_free_gb: Math.round(DEMO_SPECS.disk_total_gb * 0.36),
    disk_total_gb: DEMO_SPECS.disk_total_gb,
    nfs_free_gb: 1420,
    nfs_total_gb: 3840,
    storage_gb: wave(seed + 'g', 240, 40, 283),
    cpu_cores: DEMO_SPECS.cpu_cores,
    ram_total_gb: DEMO_SPECS.ram_total_gb,
  };
}

/* Лічильники у шапці «Системи» — щоб черга не стояла порожня. */
export function fakeQueue() {
  return {
    celery_queued: Math.max(1, Math.round(wave('queue', 46, 22, 97))),
    versions_processing: Math.max(1, Math.round(wave('proc', 12, 6, 151))),
  };
}

export function isDemo() {
  try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
}

export function setDemo(on) {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch (e) {}
  applyPalette(on);
  try { window.dispatchEvent(new CustomEvent(EV, { detail: !!on })); } catch (e) {}
}

/* Палітра. Кольори в коді записані як `var(--ac1, #7c4dff)` — тобто без
   заданої змінної лишається рівно той самий бузковий, що й був. Демо-режим
   лише підставляє інші значення.

   🔴 Ободок n8n-сервера навмисно НЕ через змінну: OWNER просив лишити
   його бузковим, щоб на демонстрації було видно, який сервер головний. */
export function applyPalette(on) {
  try {
    const r = document.documentElement;
    if (on) {
      r.style.setProperty('--ac1', '#00e5ff');
      r.style.setProperty('--ac2', '#7fe7ff');
      r.style.setProperty('--ac1rgb', '0, 229, 255');
      r.style.setProperty('--ac1d', '#00b8d4');   // темніший — для hover
    } else {
      r.style.removeProperty('--ac1');
      r.style.removeProperty('--ac2');
      r.style.removeProperty('--ac1rgb');
      r.style.removeProperty('--ac1d');
    }
  } catch (e) {}
}

/* Підписка для компонентів: повертає поточний стан і оновлюється на зміну.
   Окремої бібліотеки станів тут не заводимо — режим бачать три місця. */
export function useDemo() {
  const [on, setOn] = React.useState(isDemo);
  React.useEffect(() => {
    const h = (e) => setOn(!!(e && e.detail));
    window.addEventListener(EV, h);
    return () => window.removeEventListener(EV, h);
  }, []);
  return on;
}

/* ── Підставна черга обробки ────────────────────────────────────────
   Сторінка «Черга» показує, хто саме зараз обробляє відео. Порожня вона
   на демонстрації означала б, що продуктом ніхто не користується.

   🔴 Імена вигадані й не перетинаються з реальними: показувати гостю
   справжні нікнейми клієнтів не можна, і це не питання смаку. */
const DEMO_USERS = [
  // 🔴 Перша версія була одноманітна: слов'янське ім'я + відео-суфікс
  // (`andriy_vlog`, `olena_shorts`, `maxreels`). Двадцять таких підряд
  // видно одразу — жива база так не виглядає. Тут навмисно перемішані
  // мови, стилі й довжини: продукт міжнародний, і в черзі поруч стоять
  // українець, поляк, індієць і хтось із нікнеймом із трьох літер.
  'kovalenko', 'm_petrenko', 'dariiaa', 'o.tkachuk', 'bohdan93',
  'mike.j', 'sarah_k', 'tomhanks22', 'jdoe_nyc', 'emily.r',
  'kacper_w', 'agnieszka99', 'piotr.nowak', 'zofia_pl',
  'lukas_m', 'hannah.b', 'jonas87', 'mila_de',
  'carlos.mtz', 'lucia_es', 'diego77', 'valeria.g',
  'rahul_s', 'priya.k', 'arjun2k', 'neha_m',
  'ahmet.y', 'zeynep_tr', 'mehmet34',
  'k1ra', 'nxt', 'zed', 'vvv', 'q_7',
  'user_48210', 'id9930112', 'tg_55418', 'acc_7731',
  'studio.one', 'brightmedia', 'northpost', 'papercraneco',
  'longnickname_that_barely_fits', 'a_very_long_handle_2026',
  'v.morozova', 'ihor__k', 'yana.b', 's_hrytsenko', 'nazar_ua',
  'ff_max', 'lena0207', 'roman.k', 'anton_v', 'iryna77',
];

// У Telegram юзернейм необов'язковий. У справжній черзі частина рядків
// показує лише id — без цього список виглядає надто причесаним.
const DEMO_NO_NAME = 0.12;

// 🔴 Крок вибору імені має бути взаємно простим із розміром пулу —
// інакше слоти почнуть потрапляти в ті самі індекси і в черзі поруч
// стоятиме один нікнейм двічі. Перша версія брала індекс просто з хешу:
// у кадрі з 28 задач виходило 17 різних імен замість 28.
const STEP_COPRIME = 7;

/* Маска нікнейма для демонстрації.

   🔴 Навіщо, якщо імена й так вигадані: 54 нікнейми — це 54 шанси, що
   якийсь із них випадково існує в Telegram. Гість, побачивши «клієнта»,
   може спробувати йому написати — і потрапить до незнайомої людини, яка
   до нас не має стосунку. Маска прибирає цей ризик повністю.

   Голова — рівно 4 символи, як просив OWNER. Хвіст обмежений вісьмома
   зірочками: без обмеження `longnickname_that_barely_fits` розтягнув би
   картку на пів екрана. Мінімум три — щоб коротке ім'я на кшталт `vvv`
   теж читалось як приховане, а не як повне. */
export function maskName(name) {
  const n = String(name || '');
  if (!n) return '';
  const head = n.slice(0, 4);
  const tail = Math.min(8, Math.max(3, n.length - 4));
  return head + '*'.repeat(tail);
}
const DEMO_WORKERS = ['n8n-server', 'worker-2', 'worker-5', 'staging', 'netcup-1'];
const DEMO_RES = ['1080x1920', '720x1280', '1920x1080', '1080x1350',
                  '1440x1080', '2160x3840', '640x1136', '1280x720'];
const DEMO_MODES = ['standard', 'instant', 'prof'];

function hash32(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/* Скільки задач у роботі просто зараз — теж «дихає», інакше незмінне
   число з часом почало б виглядати як застигла картинка. */
export function fakeQueueSize() {
  // [D-432] Втричі більше за першу версію — OWNER просив, щоб черга
  // справляла враження завантаженої системи.
  return Math.max(14, Math.round(wave('qsize', 22, 7, 233)));
}

/* Черга. Кожен слот живе своїм циклом: задача йде `dur` секунд, потім на
   її місце стає нова — з іншим юзером, іншим відео, іншим воркером.

   🔴 `elapsed` рахується від годинника, а не зберігається в стані. Тому
   секунди тікають рівно й правильно навіть тоді, коли сторінку відкрили
   щойно: жодного «0с» на старті й жодних стрибків при оновленні раз на
   три секунди. */
export function fakeQueueList() {
  const t = Date.now() / 1000;
  const n = fakeQueueSize();
  const out = [];
  for (let i = 0; i < n; i++) {
    const h = hash32('slot' + i);
    const dur = 180 + (h % 7) * 65;            // 3–10 хв на задачу
    const shift = (h % 1000) / 1000 * dur;
    const cycle = Math.floor((t + shift) / dur);
    const elapsed = Math.floor((t + shift) % dur);
    const c = hash32('c' + i + '_' + cycle);
    out.push({
      task_id: 'demo-' + i + '-' + cycle,
      user_id: 10000 + (c % 89000),
      // окремий хеш на кожне рішення: побітовий зсув того самого числа
      // давав перекошений розподіл — замість 12 % виходило 21 %
      username: (hash32('nn' + i + '_' + cycle) % 100) < DEMO_NO_NAME * 100
        ? ''
        // 🔴 Маскуємо ТУТ, у генераторі, а не при показі: так повне
        // ім'я взагалі не потрапляє в дані сторінки, і його неможливо
        // випадково відкрити десь іще.
        : maskName(DEMO_USERS[(i * STEP_COPRIME + cycle) % DEMO_USERS.length]),
      worker: DEMO_WORKERS[(c >> 5) % DEMO_WORKERS.length],
      duration_sec: 9 + (c % 280),
      resolution: DEMO_RES[(c >> 7) % DEMO_RES.length],
      versions: 1 + (c % 12),
      mode: DEMO_MODES[(c >> 9) % DEMO_MODES.length],
      elapsed_sec: elapsed,
    });
  }
  // свіжі — вгорі, як і в справжній черзі
  return out.sort((a, b) => a.elapsed_sec - b.elapsed_sec);
}

// відновлюємо палітру одразу при завантаженні сторінки
applyPalette(isDemo());
